import React, { useState, useEffect } from "react";
import { Popover, Box, Typography, Avatar, CircularProgress, useTheme } from "@mui/material";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

function agentColor(name) {
  // Deterministic, not random — the same agent always gets the same color,
  // which is what makes it useful as a visual identifier at a glance.
  const palette = ["#1f7a6c", "#2f6f9e", "#8a5cb8", "#b8631f", "#4a7c59", "#a4372a"];
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

/**
 * Agent picker for Transfer — fetched fresh on every open, never cached: the
 * whole point is showing who's ACTUALLY online and free right now, and
 * that's exactly the kind of thing that goes stale between clicks.
 *
 * Explicit high z-index (see sx below) is load-bearing, not decoration:
 * CallWidget sits at theme.zIndex.snackbar + 1 so it floats above the rest
 * of the app, which is ABOVE MUI's Popover default (theme.zIndex.modal) —
 * without overriding it here, this menu rendered visually underneath the
 * widget it's anchored to.
 */
export default function TransferMenu({ anchorEl, onClose, onSelect }) {
  const theme = useTheme();
  const [agents, setAgents] = useState(null); // null = not yet loaded
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = await getAuthToken();
        const res = await axios.get(`${SERVER_DOMAIN}/api/calling/live-agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAgents(res.data?.data || []);
      } catch (err) {
        if (!cancelled) setAgents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            width: 280,
            maxHeight: 360,
            borderRadius: 2.5,
            overflow: "hidden",
            bgcolor: theme.palette.mode === "dark" ? "#1c2320" : "#ffffff",
            boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
          },
        },
      }}
      sx={{ zIndex: theme.zIndex.snackbar + 2 }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary" }}>
          Transfer to
        </Typography>
      </Box>

      <Box sx={{ maxHeight: 300, overflowY: "auto", py: 0.5 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!loading && agents?.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No agents available right now</Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 0.5 }}>
              Agents must be online and free of another call to receive a transfer.
            </Typography>
          </Box>
        )}

        {!loading &&
          agents?.map((agent) => (
            <Box
              key={agent.agentId}
              onClick={() => {
                onSelect(agent);
                onClose();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {/* Online-status dot moved onto the avatar's own corner —
                  same convention InboxSettings.jsx's Team Member rows use —
                  instead of sitting alone on the row's right edge. */}
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar sx={{ width: 30, height: 30, fontSize: 12.5, fontWeight: 600, bgcolor: agentColor(agent.name) }}>
                  {(agent.name || "?").slice(0, 2).toUpperCase()}
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -1,
                    right: -1,
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: "#2fbf8f",
                    border: (theme) => `2px solid ${theme.palette.mode === "dark" ? "#1c2320" : "#ffffff"}`,
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {agent.name}
                </Typography>
              </Box>
            </Box>
          ))}
      </Box>
    </Popover>
  );
}
